import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchInput } from "../components/SearchInput";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadRTOCodes } from "../utils/dataLoader";
import type { RTOCode } from "../types/data";

export function RtoCodesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  const { data: rtoCodes, loading, error } = useDataLoader(loadRTOCodes);

  // Debounce search term to avoid lagging while typing
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  const filteredRtoCodes = useMemo(() => {
    const source = rtoCodes ?? [];
    if (!debouncedSearch) return source;

    const term = debouncedSearch.toLowerCase();
    return source.filter(
      (code) =>
        code.code.toLowerCase().includes(term) ||
        code.city.toLowerCase().includes(term) ||
        code.state.toLowerCase().includes(term) ||
        code.fullName.toLowerCase().includes(term)
    );
  }, [debouncedSearch, rtoCodes]);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian RTO Codes Database",
    "description": "Complete database of Indian RTO (Regional Transport Office) codes for vehicle registration with state and location details",
    "keywords": ["RTO codes", "vehicle registration", "RTO office finder", "Indian vehicle codes"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/rto-codes`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      <SEO
        {...routeSeo('rto-codes')}
        keywords="RTO codes, vehicle registration, RTO office finder, vehicle state code search, RTO database"
        structuredData={structuredData}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">RTO Codes</h1>
        <p className="text-sm sm:text-base text-gray-600">Search for vehicle registration codes by state or city</p>
      </div>

      {loading && (
        <div className="text-center py-8 text-gray-500">Loading RTO codes...</div>
      )}
      {error && (
        <div className="text-center py-8 text-red-600">Failed to load RTO codes. Please try again later.</div>
      )}

      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search RTO codes, cities, or states..."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredRtoCodes.map((code) => (
          <Card key={code.code} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="text-2xl font-bold text-orange-600">{code.code}</span>
                <Badge variant="secondary">{code.state}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">State</p>
                  <p className="font-medium">{code.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">City</p>
                  <p className="font-medium">{code.city}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredRtoCodes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No RTO codes found matching your search.</p>
        </div>
      )}
    </div>
    </>
  );
}